import React from 'react';
import {
    Box,
    type IconButtonProps,
    type SxProps,
    type Theme,
    type TooltipProps,
} from '@mui/material';
import {
    checkIcon,
    chevronLeftIcon,
    chevronRightIcon,
    deleteIcon,
    downloadIcon,
    downloadSignedIcon,
    editIcon,
    mailIcon,
    moveIcon,
    preview as viewIcon,
    signatureIcon,
    uploadIcon,
    refreshDataIcon,
    splitDosDocIcon,
    type NamedIconsType,
} from '@europrocurement/l2d-icons';
import { FlexyIconButton } from '../../atoms/FlexyIconButton/FlexyIconButton';
import { useL2DTheme } from '../../../hooks/useL2DTheme';

// ── action registry ────────────────────────────────────────────────────────────

type ActionKey =
    | 'expand'
    | 'validate'
    | 'show'
    | 'sign'
    | 'downloadSigned'
    | 'download'
    | 'upload'
    | 'edit'
    | 'sendMail'
    | 'move'
    | 'delete'
    | 'refresh'
    | 'splitDosDoc';

// ── derived prop types ─────────────────────────────────────────────────────────

type ActionHandlerProps = {
    [K in ActionKey as `on${Capitalize<K>}`]?: IconButtonProps['onClick'];
};

type ActionTooltipProps = {
    [K in ActionKey as `${K}Tooltip`]?: string;
};

type ActionIconProps = {
    [K in ActionKey as `${K}Icon`]?: NamedIconsType;
};

export type ItemActionsProps = ActionHandlerProps &
    ActionTooltipProps &
    ActionIconProps & {
        /** Controls the icon shown for onExpand: true = chevronLeft (collapse), false = chevronRight (expand) */
        expanded?: boolean;
        /** Button size in px (default: 24) */
        size?: number;
        /** Icon font-size (default: '11px') */
        iconSize?: string;
        /**
         * Controls the slide animation.
         * When provided, the group fades/slides in (visible=true) or out (visible=false).
         * When omitted, the group is always rendered without animation.
         */
        visible?: boolean;
        /**
         * Side the group slides in from when becoming visible (default: 'left').
         * 'left'  → starts at translateX(-8px), slides rightward.
         * 'right' → starts at translateX(+8px), slides leftward.
         */
        slideDirection?: 'left' | 'right';
        orientation?: 'row' | 'column';
        /** Override the tooltip placement. Defaults to 'right' for column, 'bottom' for row. */
        tooltipPlacement?: TooltipProps['placement'];
        disabled?: IconButtonProps['disabled'];
        confirmation?: boolean;
        sx?: SxProps<Theme>;
        'data-testid'?: string;
    };

// ── action configs ─────────────────────────────────────────────────────────────

type ThemePalette = ReturnType<typeof useL2DTheme>['palette'];

type ActionConfig = {
    key: ActionKey;
    defaultIcon: NamedIconsType | ((props: ItemActionsProps) => NamedIconsType);
    hoverColor?: (palette: ThemePalette) => string | undefined;
    confirmationHoverColor?: (palette: ThemePalette) => string | undefined;
    isConfirmable?: true;
    fixedConfirmationTooltip?: string;
};

const ACTION_CONFIGS: ActionConfig[] = [
    {
        key: 'expand',
        defaultIcon: (props) => (props.expanded ? chevronLeftIcon : chevronRightIcon),
    },
    {
        key: 'validate',
        defaultIcon: checkIcon,
        hoverColor: (p) => p.crudAction?.validate?.textColor,
    },
    {
        key: 'show',
        defaultIcon: viewIcon,
        hoverColor: (p) => p.crudAction?.view?.textColor,
    },
    {
        key: 'sign',
        defaultIcon: signatureIcon,
        hoverColor: (p) => p.crudAction?.sign?.textColor,
    },
    {
        key: 'downloadSigned',
        defaultIcon: downloadSignedIcon,
        hoverColor: (p) => p.crudAction?.downloadSigned?.textColor,
    },
    {
        key: 'download',
        defaultIcon: downloadIcon,
        hoverColor: (p) => p.crudAction?.download?.textColor,
    },
    {
        key: 'upload',
        defaultIcon: uploadIcon,
        hoverColor: (p) => p.crudAction?.upload?.textColor,
    },
    {
        key: 'edit',
        defaultIcon: editIcon,
        hoverColor: (p) => p.crudAction?.edit?.textColor,
    },
    {
        key: 'sendMail',
        defaultIcon: mailIcon,
    },
    {
        key: 'move',
        defaultIcon: moveIcon,
        hoverColor: (p) => p.crudAction?.move?.textColor,
    },
    {
        key: 'delete',
        defaultIcon: deleteIcon,
        hoverColor: (p) => p.crudAction?.delete?.textColor,
        confirmationHoverColor: (p) => p.crudAction?.confirmation?.textColor,
        isConfirmable: true,
        fixedConfirmationTooltip: 'Cliquez pour confirmer la suppression',
    },
    {
        key: 'refresh',
        defaultIcon: refreshDataIcon,
    },
    {
        key: 'splitDosDoc',
        defaultIcon: splitDosDocIcon,
    },
];

// ── component ──────────────────────────────────────────────────────────────────

const capitalizeFirst = (s: string): string =>
    s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);

export const ItemActions: React.FunctionComponent<ItemActionsProps> = function (props) {
    const {
        expanded = false,
        size = 24,
        iconSize = '11px',
        visible,
        slideDirection = 'left',
        orientation = 'row',
        tooltipPlacement,
        disabled = false,
        confirmation = false,
        sx,
        'data-testid': dataTestId,
    } = props;

    const { palette } = useL2DTheme();

    const buttonSx = () => ({
        width: size,
        height: size,
        padding: '4px',
        borderRadius: '4px',
        transition: 'all 0.2s ease-in-out',
    });

    const tp = tooltipPlacement ?? (orientation === 'column' ? 'right' : 'bottom');
    const hasAnimation = visible !== undefined;
    const slideOffset = slideDirection === 'right' ? '8px' : '-8px';

    const propsRecord = props as Record<string, unknown>;

    return (
        <Box
            data-testid={dataTestId}
            sx={{
                display: 'flex',
                flexDirection: orientation,
                alignItems: 'center',
                gap: '4px',
                ...sx,
                ...(hasAnimation && {
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateX(0)' : `translateX(${slideOffset})`,
                    transition:
                        'opacity 0.25s ease, transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    pointerEvents: visible ? 'auto' : 'none',
                }),
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {ACTION_CONFIGS.map(
                ({
                    key,
                    defaultIcon,
                    hoverColor,
                    confirmationHoverColor,
                    isConfirmable,
                    fixedConfirmationTooltip,
                }) => {
                    const onAction = propsRecord[`on${capitalizeFirst(key)}`] as
                        | IconButtonProps['onClick']
                        | undefined;
                    if (!onAction) return null;

                    const tooltip = propsRecord[`${key}Tooltip`] as string | undefined;
                    const iconOverride = propsRecord[`${key}Icon`] as NamedIconsType | undefined;
                    const resolvedIcon =
                        iconOverride ||
                        (typeof defaultIcon === 'function'
                            ? defaultIcon({ ...props, expanded })
                            : defaultIcon);

                    return (
                        <FlexyIconButton
                            key={key}
                            icon={resolvedIcon}
                            tooltip={tooltip}
                            tooltipPlacement={tp}
                            normalHoverColor={hoverColor ? hoverColor(palette) : undefined}
                            confirmationHoverColor={
                                confirmationHoverColor ? confirmationHoverColor(palette) : undefined
                            }
                            onClick={onAction}
                            buttonOverwriteProps={{ sx: buttonSx() }}
                            iconOverwriteProps={{ sx: { fontSize: iconSize } }}
                            disabled={disabled}
                            confirmation={isConfirmable ? confirmation : false}
                            confirmationTooltip={
                                isConfirmable ? fixedConfirmationTooltip : undefined
                            }
                        />
                    );
                },
            )}
        </Box>
    );
};
